import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, Loader2, CheckCircle, Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_caregiver-hub-25/artifacts/7i9n9nvr_Zorg%20Vleugel%20Logo%20Icoon%20Rood.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      
      // Check if email was sent or if we got a direct link
      if (response.data.reset_url) {
        setResetUrl(response.data.reset_url);
        setEmailSent(false);
      } else {
        setEmailSent(response.data.email_sent || false);
      }
      
      toast.success('Reset link aangemaakt!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSent(true);
      setEmailSent(false);
      toast.success('Als dit e-mailadres bij ons bekend is, ontvang je een reset link.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (resetUrl) {
      navigator.clipboard.writeText(resetUrl);
      setCopied(true);
      toast.success('Link gekopieerd!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={LOGO_URL} alt="Zorg-Vleugel" className="h-16 w-16" />
          </div>

          {!sent ? (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">
                  Wachtwoord Vergeten?
                </h1>
                <p className="text-slate-600">
                  Vul je e-mailadres in om je wachtwoord te resetten.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">
                    E-mailadres
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="naam@zorg-vleugel.nl"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="h-12 pl-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87] hover:from-[#2d5a87] hover:to-[#3d6a97] shadow-lg shadow-[#1e3a5f]/25 transition-all duration-300"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Verstuur Reset Link'
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Success State */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-6"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">
                  Reset Link Aangemaakt
                </h2>
                <p className="text-slate-600">
                  {emailSent 
                    ? `We hebben een e-mail gestuurd naar ${email} met instructies om je wachtwoord te resetten.`
                    : resetUrl 
                      ? `Gebruik de onderstaande link om je wachtwoord te resetten.`
                      : `Als dit e-mailadres bij ons bekend is, ontvang je een reset link via e-mail zodra dit is geconfigureerd.`
                  }
                </p>
              </div>

              {/* Show reset link if email wasn't sent */}
              {resetUrl && !emailSent && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>Let op:</strong> E-mail verzending is nog niet actief. Gebruik de link hieronder:
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-slate-500 mb-2">Reset Link (1 uur geldig):</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs text-slate-700 break-all bg-slate-50 p-2 rounded">
                        {resetUrl}
                      </code>
                      <button
                        onClick={handleCopyLink}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                        title="Kopieer link"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    onClick={() => window.open(resetUrl, '_blank')}
                    className="w-full h-10 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700"
                  >
                    Open Reset Pagina
                  </Button>
                </div>
              )}

              {emailSent && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Check ook je spam folder als je de e-mail niet ziet.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Back to Login */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#2d5a87] font-medium transition-colors"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar inloggen
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          © 2026 Zorg-Vleugel • Begeleid Wonen
        </p>
      </motion.div>
    </div>
  );
}
